import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { testimonials, faqList } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9p0ql2ftmtnfcfh0fh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: '关于我们 - 韩漫网站',
        description: '韩漫网站品牌介绍、用户口碑与联系方式',
        url: 'https://www.hanman.site/about',
        isPartOf: {
          '@type': 'WebSite',
          name: '韩漫网站',
          url: 'https://www.hanman.site',
        },
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqList.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <Layout>
      <SEOHead
        title="关于我们 - 韩漫网站 - 好看的韩国漫画免费阅读平台"
        description="韩漫网站品牌介绍，汇聚众多精彩韩漫作品，免费阅读配合流畅下拉式浏览，致力于为漫画爱好者提供优质的在线阅读体验。"
        keywords="韩漫网站,关于我们,韩漫,韩国漫画,联系我们"
        canonicalUrl="https://www.hanman.site/about"
      />
      <SchemaOrg schema={aboutSchema} />

      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '关于我们' },
        ]}
      />

      <section className="w-full py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于韩漫网站
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              我们致力于为广大漫画爱好者提供最优质的韩国漫画阅读体验
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-12 mb-16">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                品牌故事
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  韩漫网站成立于2024年，由一群热爱韩国漫画的年轻人共同创建。我们看到了国内读者对优质韩漫内容的强烈需求，决定打造一个专注、专业的韩漫阅读平台。
                </p>
                <p>
                  经过两年多的发展，韩漫网站已经汇聚了数百部精选韩漫作品，涵盖恋爱、奇幻、动作、悬疑、古风等多个类型。我们始终坚持免费阅读的初心，让每一位漫画爱好者都能无障碍地享受韩漫的魅力。
                </p>
                <p>
                  我们独创的下拉式阅读模式，让读者无需频繁翻页，只需轻轻滑动即可沉浸于精彩的漫画世界中。智能加载技术确保在各类设备上都能获得流畅的阅读体验。
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                我们的优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-book-read-line', title: '海量作品', desc: '数百部精选韩漫，持续更新中' },
                  { icon: 'ri-money-cny-circle-line', title: '完全免费', desc: '无需付费，畅享全部内容' },
                  { icon: 'ri-smartphone-line', title: '多端适配', desc: '手机、平板、电脑完美兼容' },
                  { icon: 'ri-speed-line', title: '流畅阅读', desc: '下拉式浏览，智能加载技术' },
                  { icon: 'ri-translate', title: '优质翻译', desc: '专业团队，精准本地化' },
                  { icon: 'ri-time-line', title: '定时更新', desc: '每周准时更新，追更不等待' },
                ].map((item, idx) => (
                  <div key={idx} className="p-4 rounded-lg bg-background-100">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h3>
                    <p className="text-xs text-foreground-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              读者口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((t, idx) => (
                <div key={idx} className="p-5 rounded-lg bg-background-100">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-xs ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    &ldquo;{t.comment}&rdquo;
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">—— {t.name}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="bg-background-100 rounded-lg p-6 md:p-8">
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-5"
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-hp-field"
                />

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-2">
                    您的称呼
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的称呼"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-2">
                    电子邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-2">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                    placeholder="请输入您的留言或建议（不超过500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                {formStatus === 'error' && formError && (
                  <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm flex items-start gap-2">
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-line"></i>
                    </span>
                    <span>{formError}</span>
                  </div>
                )}

                {formStatus === 'success' && (
                  <div className="p-3 rounded-lg bg-secondary-50 text-secondary-700 text-sm flex items-start gap-2">
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-line"></i>
                    </span>
                    <span>感谢您的留言，我们会尽快回复！</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full px-6 py-3 bg-primary-500 text-white rounded-lg font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
                >
                  {formStatus === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin"></i>
                      </span>
                      提交中...
                    </span>
                  ) : (
                    '发送留言'
                  )}
                </button>
              </form>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div className="p-4 rounded-lg bg-background-100">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-500">contact@hanman.site</p>
              </div>
              <div className="p-4 rounded-lg bg-background-100">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">服务时间</h3>
                <p className="text-xs text-foreground-500">7×24小时</p>
              </div>
              <div className="p-4 rounded-lg bg-background-100">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-chat-smile-2-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">快速响应</h3>
                <p className="text-xs text-foreground-500">24小时内回复</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}